import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { buttonHover } from '../utils/animations';

const TypingAnimation = () => (
  <div className="flex items-center space-x-2 mb-2">
    <span className="text-gray-500">Менеджер печатает</span>
    <div className="flex space-x-1">
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
    </div>
  </div>
);

const ChatWindow: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(localStorage.getItem('chatName') || '');
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<string[]>(['Менеджер: Добрый день! Как мы можем вам помочь?']);
  const [uniqueId] = useState(() => `id-${Date.now()}`);
  const [isNameLocked, setIsNameLocked] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const sendMessage = async () => {
    if (!name) {
      alert('Представьтесь, пожалуйста.');
      return;
    }

    const newMessage = `${name}: ${message}`;
    const updatedHistory = [...chatHistory, newMessage];
    
    // Immediately show user's message and typing animation
    setChatHistory(updatedHistory);
    setMessage(''); // Clear the message input after sending
    setIsNameLocked(true); // Lock the name after first message
    setIsTyping(true); // Show typing animation

    const payload = {
      unic_id: uniqueId,
      name,
      history: updatedHistory,
    };

    try {
      console.log('Sending request...', new Date().toISOString());
      const response = await fetch('https://kot.internal.haninetwork.com/webhook/beef6f3d-1009-4d5c-8b35-bbb8715a1376', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      console.log('Response received...', new Date().toISOString());
      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        responseData = [{ output: text }];
      }
      console.log('Response data:', responseData);
      
      // Hide typing animation and add manager's response
      setIsTyping(false);
      
      // Add the response from the webhook to chat history
      if (responseData && responseData[0] && responseData[0].output) {
        console.log('Adding response to chat...', new Date().toISOString());
        setChatHistory((prevHistory) => [...prevHistory, `Менеджер: ${responseData[0].output}`]);
      }

      // Save the name to localStorage
      localStorage.setItem('chatName', name);
    } catch (error) {
      console.error('Error sending message:', error);
      // Hide typing animation and show error message
      setIsTyping(false);
      setChatHistory((prevHistory) => [...prevHistory, 'Система: Произошла ошибка при отправке сообщения. Пожалуйста, свяжитесь с нами по телефону или электронной почте mail@itjoy.space.']);
    }
  };

  return (
    <div className="fixed bottom-4 right-4">
      {!isOpen && (
        <motion.button
          onClick={toggleChat}
          className="bg-secondary text-white p-3 rounded-full relative"
          whileHover={buttonHover}
        >
          Чат поддержки
          {chatHistory.length > 0 && !isOpen && (
            <span className="absolute -top-2 -right-2 bg-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
              <span className="text-secondary transform transition-transform duration-500 ease-in-out animate-pulse">
                {chatHistory.length}
              </span>
            </span>
          )}
        </motion.button>
      )}
      {isOpen && (
        <div className="bg-white p-4 rounded-lg shadow-lg max-h-[80vh] w-full md:w-[33vw] md:min-w-[380px] overflow-hidden fixed bottom-4 right-4 left-4 md:left-auto">
          <h2 className="text-lg font-bold mb-4">Добрый день! Ответим на ваши вопросы</h2>
          <div className="chat-history mb-4 overflow-y-auto max-h-[50vh]">
            {chatHistory.map((msg, index) => (
              <div key={index} className="mb-2 break-words">
                {msg}
              </div>
            ))}
            {isTyping && <TypingAnimation />}
          </div>
          <div className="input-area">
            {!isNameLocked && (
              <input
                type="text"
                placeholder="Ваше имя"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 mb-2 border rounded"
              />
            )}
            <textarea
              placeholder="Ваш вопрос"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                e.target.style.height = 'auto';
                const maxHeight = 4 * parseFloat(getComputedStyle(e.target).lineHeight);
                e.target.style.height = Math.min(e.target.scrollHeight, maxHeight) + 'px';
              }}
              className="w-full p-2 border rounded resize-none overflow-y-auto"
            />
            <button onClick={sendMessage} className="bg-secondary text-white p-2 mt-2 rounded w-full mb-2">
              Отправить
            </button>
            <button onClick={toggleChat} className="bg-white text-secondary p-2 rounded w-full border border-secondary">
              Закрыть
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow; 